import * as turf from '@turf/turf';
import { Maximize01, Maximize02, Minimize02, Minus, Plus } from '@untitled-ui/icons-react/build/cjs';
import mapboxgl from 'mapbox-gl';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import startMarkerImg from 'src/assets/img/pinWithParcel.svg';
import { logError } from 'src/services/LoggingService';

import 'mapbox-gl/dist/mapbox-gl.css';

interface MapboxProps {
    startPoint: { lat: number; lng: number; name: string | null };
    endPoint?: { lat: number; lng: number; name: string | null };
    midpoint?: { lat: number; lng: number; name: string | null };
    midpoints?: {
        lat: number;
        lng: number;
        name: string | null;
        type?: string;
        // Task 28/29 (client decision) — pickup/drop detail + stop number shown in the stop
        // marker's hover tooltip. Additive/optional so every other existing midpoints caller
        // (none of which pass these) is unaffected.
        position?: number;
        unitsOn?: number;
        unitsOff?: number;
    }[];
    height?: any;
    className?: string;
    markerRef?: any;
    isShowRoute?: boolean;
    isZoomControlVisible?: boolean;
    isFullscreen?: boolean;
    isDraggable?: boolean;
    isZoomable?: boolean;
    defaultZoom?: any;
    setDistance?: any;
    setDuration?: any;
    showImageAsMarker?: boolean;
    currentLocation?: any;
    preventCrossBorder?: boolean; // New prop to control cross-border routing
    setIsRouteLoading?: any;
    showMapControls?: boolean; // Show custom zoom and fullscreen controls
    startMarkerColor?: string; // Color for the start point marker
    endMarkerColor?: string; // Color for the end point marker
    useStartMarkerIcon?: boolean;
    showZoomControls?: boolean;
    showMidpointMarkers?: boolean; // Show markers for midpoints (default: true)
    showStartMarker?: boolean; // Show marker for start point (default: true)
    useSimpleStartMarker?: boolean; // Use simple dot marker for start point instead of icon (default: false)
    showEndPointTooltip?: boolean; // Show tooltip for end point (default: false)
}

const calculateBearing = (startLat: number, startLng: number, endLat: number, endLng: number): number => {
    const startLatRad = (startLat * Math.PI) / 180;
    const startLngRad = (startLng * Math.PI) / 180;
    const endLatRad = (endLat * Math.PI) / 180;
    const endLngRad = (endLng * Math.PI) / 180;

    const dLng = endLngRad - startLngRad;

    const y = Math.sin(dLng) * Math.cos(endLatRad);
    const x = Math.cos(startLatRad) * Math.sin(endLatRad) - Math.sin(startLatRad) * Math.cos(endLatRad) * Math.cos(dLng);

    let bearing = Math.atan2(y, x);
    bearing = (bearing * 180) / Math.PI;
    bearing = (bearing + 360) % 360;

    return bearing;
};

const createSVGMarker = (borderColor: string, rotationAngle: number = 0) => {
    const svgElement = document.createElement('div');
    svgElement.className = 'parcel-arrow';

    // Create the marker container with inline styles for the border
    const markerContainer = document.createElement('div');
    markerContainer.style.width = '12px';
    markerContainer.style.height = '14px';
    markerContainer.style.position = 'relative';

    // Create the pseudo-element effect using a real element with inline styles
    const borderElement = document.createElement('div');
    borderElement.style.content = '';
    borderElement.style.width = '24px';
    borderElement.style.height = '24px';
    borderElement.style.borderRadius = '50%';
    borderElement.style.position = 'absolute';
    borderElement.style.top = '50%';
    borderElement.style.left = '50%';
    borderElement.style.transform = 'translate(-50%, -50%)';
    borderElement.style.zIndex = '-1';
    borderElement.style.backgroundColor = 'transparent';

    const svgElement2 = document.createElement('div');
    svgElement2.innerHTML = ` <div class="w-[12px]">
      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="25" viewBox="0 0 24 25" fill="none" class="w-full" style="width: 100%; transform: rotate(${rotationAngle}deg);width: 12px; height: 14px;">
        <path d="M12.6657 2.67916C12.2439 2.4686 11.7476 2.4686 11.3258 2.67916C10.9525 2.86552 10.7595 3.19367 10.6676 3.36083C10.5675 3.543 10.4649 3.77692 10.362 4.01162L3.05073 20.6813C2.93566 20.9436 2.82421 21.1976 2.7529 21.4084C2.69129 21.5905 2.56638 21.9837 2.70011 22.4066C2.84758 22.873 3.21319 23.2382 3.67975 23.3851C4.10282 23.5184 4.49584 23.393 4.67791 23.3312C4.88863 23.2596 5.14253 23.1478 5.4047 23.0324L11.9957 20.1324L18.5868 23.0325C18.8489 23.1478 19.1028 23.2596 19.3135 23.3312C19.4956 23.393 19.8886 23.5184 20.3117 23.3851C20.7783 23.2382 21.1439 22.873 21.2913 22.4066C21.4251 21.9837 21.3002 21.5905 21.2385 21.4084C21.1672 21.1976 21.0558 20.9436 20.9407 20.6813L13.6295 4.01167C13.5266 3.77695 13.424 3.54301 13.3238 3.36083C13.2319 3.19367 13.039 2.86552 12.6657 2.67916Z" fill="white"/>
      </svg>
    </div>`;

    // Assemble the marker
    markerContainer.appendChild(borderElement);
    markerContainer.appendChild(svgElement2);
    svgElement.appendChild(markerContainer);

    return svgElement;
};

// Task 28/29 (client decision) — a distinct "stop" marker for route stops (see
// updateMidpointMarkers' midpoints branch, only ever reached when showMidpointMarkers is
// explicitly opted into — no existing caller does, so this is unreachable/risk-free for every
// other MapboxMap consumer). A simple colored dot rather than createSVGMarker's directional
// arrow, or a full map-pin shape — a stop is a location, not a heading, and the hover tooltip
// (not the icon itself) carries the detail.
const createStopMarkerElement = (fillColor: string): HTMLDivElement => {
    const container = document.createElement('div');
    container.style.width = '16px';
    container.style.height = '16px';
    container.style.borderRadius = '50%';
    container.style.backgroundColor = fillColor;
    container.style.border = '3px solid #ffffff';
    container.style.boxShadow = '0 1px 4px rgba(0, 0, 0, 0.35)';
    container.style.cursor = 'pointer';

    return container;
};

const findClosestCoord = (currentCoord: any, routeCoords: any) => {
    let minDist = Infinity;
    let closestCoord = routeCoords[0];
    routeCoords.forEach((coord: any) => {
        const dist = turf.distance(turf.point(currentCoord), turf.point(coord), {
            units: 'meters',
        });

        if (dist < minDist) {
            minDist = dist;
            closestCoord = coord;
        }
    });

    return closestCoord;
};

// Fetch driving route from Mapbox Directions API between two points
const getDrivingRouteCoordinates = async (
    start: { lat: number; lng: number },
    end: { lat: number; lng: number },
    t: any,
    preventCrossBorder: boolean = false,
    clearRoutes?: () => void,
    updatePointMarkers?: () => void
): Promise<[number, number][]> => {
    // Dynamically add exclude parameter based on preventCrossBorder prop
    const excludeParam = preventCrossBorder ? '&exclude=country_border' : '';

    const url = `https://api.mapbox.com/directions/v5/mapbox/driving/${start.lng},${start.lat};${end.lng},${end.lat}?geometries=geojson&overview=full${excludeParam}&access_token=${mapboxgl.accessToken}`;

    try {
        const res = await fetch(url);
        const data = await res.json();

        if (data.routes && data.routes.length > 0) {
            clearRoutes?.();

            updatePointMarkers?.();

            return data.routes[0].geometry.coordinates;
        }

        return [];
    } catch (error: any) {
        await logError(error, {
            errorMessage: error?.message || t('MapBox.Error_fetching_route'),
            errorLocation: 'OrderView: MapBoxMap: getDrivingRouteCoordinates',
            storeInDatabase: true,
            logToConsole: true,
        });

        return [];
    }
};

// Fetch route stats (distance & duration) separately for performance.
// Exported so callers that need real driving distance (not straight-line)
// without rendering a map can reuse it — e.g. QuotePreparingLoader's lane
// insights, which must match what this same page's Map-It widget shows.
export const getRouteStats = async (
    start: { lat: number; lng: number },
    end: { lat: number; lng: number },
    t: any,
    preventCrossBorder: boolean = false
): Promise<{ distance: number; duration: number }> => {
    // Dynamically add exclude parameter based on preventCrossBorder prop
    const excludeParam = preventCrossBorder ? '&exclude=country_border' : '';

    const url = `https://api.mapbox.com/directions/v5/mapbox/driving/${start.lng},${start.lat};${end.lng},${end.lat}?overview=false${excludeParam}&access_token=${mapboxgl.accessToken}`;

    try {
        const res = await fetch(url);
        const data = await res.json();

        if (data.routes && data.routes.length > 0) {
            return {
                distance: data.routes[0].distance,
                duration: data.routes[0].duration,
            };
        }

        return { distance: 0, duration: 0 };
    } catch (error: any) {
        await logError(error, {
            errorMessage: error?.message || t('MapBox.Error_fetching_route_stats'),
            errorLocation: 'OrderView: MapBoxMap: getRouteStats',
            storeInDatabase: true,
            logToConsole: true,
        });

        return { distance: 0, duration: 0 };
    }
};

const MapboxMap: React.FC<MapboxProps> = ({
    startPoint,
    endPoint,
    midpoint,
    midpoints,
    height = 500,
    className,
    markerRef,
    isShowRoute = false,
    isZoomControlVisible = false,
    isFullscreen = true,
    isDraggable = false,
    isZoomable = true,
    defaultZoom = 2,
    setDistance,
    setDuration,
    showImageAsMarker = false,
    currentLocation,
    preventCrossBorder = false, // Default to false to maintain backward compatibility
    setIsRouteLoading,
    showMapControls = false,
    startMarkerColor = '#444CE7', // Default blue color
    endMarkerColor = '#444CE7', // Default blue color
    useStartMarkerIcon = false,
    showZoomControls = false,
    showMidpointMarkers = true,
    showStartMarker = true,
    useSimpleStartMarker = false,
    showEndPointTooltip = false,
}) => {
    const { t } = useTranslation();
    const mapRef = useRef<any>(null);
    const mapContainerRef = useRef<any>(null);
    const containerRef = useRef<HTMLDivElement | null>(null);
    const [isFullscreenMode, setIsFullscreenMode] = useState(false);
    const [isMapLoaded, setIsMapLoaded] = useState(false);
    const startMarkerRef = useRef<mapboxgl.Marker | null>(null);
    const endMarkerRef = useRef<mapboxgl.Marker | null>(null);
    const currentLocationMarkerRef = useRef<mapboxgl.Marker | null>(null);

    // Signature of everything the route/marker redraw actually depends on, by VALUE.
    //
    // Callers hand these props down as fresh literals on every render — MapItWidget rebuilds
    // `midpoints` with `waypoints.map(...)`, and a parent re-render for an unrelated reason
    // (a tooltip opening, a widget toggling) is enough to produce new references with identical
    // coordinates. The redraw effect below was keyed on those references, so it re-ran the whole
    // pipeline for nothing: clearRoutes() wipes the line, the Directions calls refetch it and
    // fitBounds re-animates — the drawn route visibly blinks, and every pass costs billable
    // Directions requests. Comparing values instead makes an unchanged route a no-op; every input
    // that genuinely changes the drawing (coordinates, marker styling, route flags) is in here, so
    // nothing that used to trigger a redraw stops triggering one.
    const pointKey = (point?: { lat?: number; lng?: number; name?: string | null } | null): string => (point?.lat != null && point?.lng != null ? `${point.lat},${point.lng},${point.name ?? ''}` : '');

    const routeSignature = [
        pointKey(startPoint),
        pointKey(endPoint),
        pointKey(midpoint),
        (midpoints ?? []).map((mp) => `${pointKey(mp)},${mp?.position ?? ''},${mp?.unitsOn ?? ''},${mp?.unitsOff ?? ''}`).join(';'),
        pointKey(currentLocation),
        String(isShowRoute),
        String(preventCrossBorder),
        String(defaultZoom),
        String(showImageAsMarker),
        String(useStartMarkerIcon),
        String(useSimpleStartMarker),
        String(showStartMarker),
        String(showMidpointMarkers),
        String(showEndPointTooltip),
        String(startMarkerColor),
        String(endMarkerColor),
    ].join('|');

    // Route signature currently drawn on the map — null whenever no map instance holds it.
    const drawnRouteSignatureRef = useRef<string | null>(null);

    // Handle zoom in
    const handleZoomIn = useCallback(() => {
        if (mapRef.current) {
            mapRef.current.zoomIn();
        }
    }, []);

    // Handle zoom out
    const handleZoomOut = useCallback(() => {
        if (mapRef.current) {
            mapRef.current.zoomOut();
        }
    }, []);

    const handleResetZoom = useCallback(() => {
        if (!mapRef.current) return;

        const routeSource = mapRef.current.getSource('route') as mapboxgl.GeoJSONSource;
        const routeData = routeSource?._data as any;
        const routeCoords = routeData?.geometry?.coordinates;

        const bounds = new mapboxgl.LngLatBounds();

        if (routeCoords && routeCoords.length > 0) {
            routeCoords.forEach((coord: [number, number]) => bounds.extend(coord));
        } else if (startPoint?.lng && endPoint?.lng) {
            bounds.extend([startPoint.lng, startPoint.lat]);
            bounds.extend([endPoint.lng, endPoint.lat]);
        } else if (startPoint?.lng) {
            mapRef.current.flyTo({ center: [startPoint.lng, startPoint.lat], zoom: defaultZoom > 16 ? defaultZoom : 16 });

            return;
        } else if (endPoint?.lng) {
            mapRef.current.flyTo({ center: [endPoint.lng, endPoint.lat], zoom: defaultZoom > 16 ? defaultZoom : 16 });

            return;
        } else {
            return;
        }

        mapRef.current.fitBounds(bounds, {
            padding: { top: 50, bottom: 50, left: 50, right: 50 },
            maxZoom: 10,
        });
    }, [startPoint, endPoint, defaultZoom]);

    const handleMouseEnter = useCallback(() => {
        if (mapRef.current && isMapLoaded) {
            mapRef.current.scrollZoom.enable();
        }
    }, [isMapLoaded]);

    const handleMouseLeave = useCallback(() => {
        if (mapRef.current && isMapLoaded) {
            mapRef.current.scrollZoom.disable();
        }
    }, [isMapLoaded]);

    // Handle fullscreen toggle
    const toggleFullscreen = useCallback(() => {
        if (!containerRef.current) return;

        if (!document.fullscreenElement) {
            containerRef.current
                .requestFullscreen()
                .then(() => {
                    setIsFullscreenMode(true);
                })
                .catch((err) => {
                    console.error('Error attempting to enable fullscreen:', err);
                });
        } else {
            document
                .exitFullscreen()
                .then(() => {
                    setIsFullscreenMode(false);
                })
                .catch((err) => {
                    console.error('Error attempting to exit fullscreen:', err);
                });
        }
    }, []);

    // Listen for fullscreen change events
    useEffect(() => {
        const handleFullscreenChange = () => {
            const isNowFullscreen = !!document.fullscreenElement;
            setIsFullscreenMode(isNowFullscreen);

            if (mapRef.current) {
                setTimeout(() => {
                    mapRef.current?.resize();
                    handleResetZoom();

                    if (isNowFullscreen) {
                        mapRef.current?.scrollZoom.enable();
                    } else {
                        mapRef.current?.scrollZoom.disable();
                    }
                }, 100);
            }
        };

        document.addEventListener('fullscreenchange', handleFullscreenChange);

        return () => {
            document.removeEventListener('fullscreenchange', handleFullscreenChange);
        };
    }, [handleResetZoom]);

    const updateMidpointMarkers = useCallback(() => {
        if (!mapRef.current || !isShowRoute || !showMidpointMarkers) return;

        if (markerRef?.current) {
            if (Array.isArray(markerRef.current)) {
                markerRef.current.forEach((marker: mapboxgl.Marker) => marker?.remove());
            } else {
                markerRef.current.remove();
            }
            markerRef.current = null;
        }

        const routeSource = mapRef.current.getSource('route') as mapboxgl.GeoJSONSource;
        const routeData = routeSource?._data as any;
        const routeCoords = routeData?.geometry?.coordinates;

        if (midpoints && midpoints.length > 0) {
            const markers: mapboxgl.Marker[] = [];
            let newMidpointCoords: [number, number] | null = null;

            midpoints.forEach((mp) => {
                if (mp?.lat && mp?.lng) {
                    let markerElement;

                    if (showImageAsMarker) {
                        markerElement = document.createElement('div');
                        markerElement.style.width = '23px';
                        markerElement.style.height = '30px';
                        markerElement.style.backgroundImage = `url(${startMarkerImg})`;
                        markerElement.style.backgroundSize = 'contain';
                    } else {
                        let borderColor = '#6172F3';

                        if (mp.type === 'last') {
                            borderColor = '#F04438';
                        } else if (mp.type === 'new') {
                            borderColor = '#079455';
                        }

                        // Task 28 (client decision) — a stop pin instead of the directional
                        // arrow marker createSVGMarker builds.
                        markerElement = createStopMarkerElement(borderColor);
                    }

                    let markerCoords: [number, number] = [mp.lng, mp.lat];

                    if (routeCoords && routeCoords.length > 0) {
                        try {
                            const snappedCoords = findClosestCoord([mp.lng, mp.lat], routeCoords);
                            markerCoords = snappedCoords as [number, number];
                        } catch (error: any) {
                            console.warn(t('MapBox.Failed_to_snap_midpoint_to_route'), error);
                        }
                    }

                    const marker = new mapboxgl.Marker(markerElement).setLngLat(markerCoords).addTo(mapRef.current);

                    // Task 28/29 — hover tooltip (not mapboxgl's default click-to-toggle popup,
                    // which marker.setPopup() alone would give) showing what boards/departs at
                    // this stop. Mirrors the manual mouseenter/mouseleave Popup pattern already
                    // used for hover tooltips in Track/index.tsx rather than setPopup()/click.
                    // Card design (STOP N label + city/state + colored pill badges) intentionally
                    // mirrors MultiStopRouteStrip.tsx's own stop cards visually — same Tailwind
                    // utility classes BadgeCmp itself uses for style="pill" type="primary"/
                    // "success", reused directly here since this tooltip lives outside the React
                    // tree (a raw Mapbox popup) and can't render the BadgeCmp component itself.
                    // Task 30 (client decision) — wording is explicit "picked up"/"dropped" here
                    // rather than the Route strip's terser "on"/"off" (Units_On/Units_Off), so
                    // these get their own dedicated keys (Stop_Tooltip_Picked_Up/Dropped).
                    if (mp.name || mp.position || mp.unitsOn || mp.unitsOff) {
                        const badgeParts: string[] = [];

                        if (mp.unitsOn) {
                            badgeParts.push(
                                `<div class="px-[6px] py-px border inline-flex shadow items-center justify-start bg-primary50 border-primary rounded-full">
                                    <div class="text-center font-medium flex items-center capitalize text-[10px] leading-[14px] text-primary">▲ ${t('MyQuote.Stop_Tooltip_Picked_Up', {
                                        count: mp.unitsOn,
                                    } as any)}</div>
                                </div>`
                            );
                        }

                        if (mp.unitsOff) {
                            badgeParts.push(
                                `<div class="px-[6px] py-px border inline-flex shadow items-center justify-start bg-success50 border-fgSuccessPrimary rounded-full">
                                    <div class="text-center font-medium flex items-center capitalize text-[10px] leading-[14px] text-fgSuccessPrimary">▼ ${t('MyQuote.Stop_Tooltip_Dropped', {
                                        count: mp.unitsOff,
                                    } as any)}</div>
                                </div>`
                            );
                        }

                        const tooltipHtml = `
                            <div style="min-width: 130px;">
                                ${mp.position ? `<p class="text-[11px] font-semibold text-gray400 uppercase mb-0.5">${t('MyQuote.Stop_N', { n: mp.position } as any)}</p>` : ''}
                                ${mp.name ? `<p class="text-sm font-semibold mb-1.5">${mp.name}</p>` : ''}
                                ${badgeParts.length ? `<div class="flex gap-1.5 flex-wrap">${badgeParts.join('')}</div>` : ''}
                            </div>
                        `;
                        const markerEl = markerElement;

                        markerEl.addEventListener('mouseenter', () => {
                            const popup = new mapboxgl.Popup({ offset: 14, closeButton: false, closeOnClick: false })
                                .setLngLat(markerCoords)
                                .setHTML(tooltipHtml)
                                .addTo(mapRef.current as mapboxgl.Map);
                            (markerEl as any)._stopPopup = popup;
                        });
                        markerEl.addEventListener('mouseleave', () => {
                            (markerEl as any)._stopPopup?.remove();
                            (markerEl as any)._stopPopup = null;
                        });
                    }

                    markers.push(marker);

                    if (mp.type === 'new') {
                        newMidpointCoords = markerCoords;
                    }
                }
            });

            markerRef.current = markers;

            if (newMidpointCoords) {
                mapRef.current.flyTo({
                    center: newMidpointCoords,
                    zoom: 6,
                    duration: 1500,
                    essential: true,
                });
            }
        } else if (midpoint?.lat && midpoint?.lng) {
            const midpointMarker = document.createElement('div');

            if (showImageAsMarker) {
                midpointMarker.style.width = '23px';
                midpointMarker.style.height = '30px';
                midpointMarker.style.backgroundImage = `url(${startMarkerImg})`;
                midpointMarker.style.backgroundSize = 'contain';
            } else {
                midpointMarker.style.width = '10px';
                midpointMarker.style.height = '10px';
                midpointMarker.style.backgroundColor = '#444CE7';
                midpointMarker.style.borderRadius = '50%';
                midpointMarker.style.boxShadow = '0 0 5px rgba(0, 0, 0, 0.5)';
            }

            let markerCoords: [number, number] = [midpoint.lng, midpoint.lat];

            if (routeCoords && routeCoords.length > 0) {
                try {
                    const snappedCoords = findClosestCoord([midpoint.lng, midpoint.lat], routeCoords);
                    markerCoords = snappedCoords as [number, number];
                } catch (error: any) {
                    console.warn(t('MapBox.Failed_to_snap_midpoint_to_route'), error);
                }
            }

            markerRef.current = new mapboxgl.Marker(midpointMarker).setLngLat(markerCoords).addTo(mapRef.current);

            if (midpoint.name) {
                const popup = new mapboxgl.Popup({
                    offset: 25,
                    closeButton: false,
                }).setHTML(`<h3>${midpoint.name}</h3>`);
                markerRef.current.setPopup(popup);
            }
        }
    }, [midpoint, midpoints, showImageAsMarker, isShowRoute, markerRef, showMidpointMarkers]);

    const addMarker = useCallback(
        (
            map: any,
            position: any,
            imageUrl: any,
            name: any,
            showImage: any,
            markerColor: string = '#444CE7',
            useIcon: boolean = false,
            rotationAngle: number = 0,
            showTooltip: boolean = false
        ): mapboxgl.Marker | null => {
            if (!position || !position.lng || !position.lat) return null;

            let markerElement;

            if (useIcon) {
                markerElement = createSVGMarker(markerColor, rotationAngle);
            } else if (showImage) {
                markerElement = document.createElement('div');
                markerElement.style.width = '23px';
                markerElement.style.height = '30px';
                markerElement.style.backgroundImage = `url(${imageUrl})`;
                markerElement.style.backgroundSize = 'contain';
            } else {
                markerElement = document.createElement('div');
                markerElement.style.width = '10px';
                markerElement.style.height = '10px';
                markerElement.style.backgroundColor = markerColor;
                markerElement.style.borderRadius = '50%';
                markerElement.style.boxShadow = '0 0 5px rgba(0, 0, 0, 0.5)';
            }

            const marker = new mapboxgl.Marker(markerElement).setLngLat([position.lng, position.lat]).addTo(map);

            if (showTooltip && name) {
                const popup = new mapboxgl.Popup({
                    offset: [0, -8],
                    closeButton: false,
                    className: 'map-marker-popup',
                    closeOnClick: false,
                    anchor: 'bottom',
                }).setHTML(
                    `<div style="
                        font-size: 12px;
                        font-weight: 500;
                        color: #F9F9F9;
                        background: #0C111B;
                        padding: 4px 8px;
                        border-radius: 8px;
                        border: 1px solid #0C111B;
                        white-space: nowrap;
                        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
                        line-height: 1.4;
                    ">${name}</div>`
                );
                marker.setPopup(popup);
                marker.togglePopup();
            }

            return marker;
        },
        []
    );

    // Update start/end markers without reinitializing the map
    const updatePointMarkers = useCallback(() => {
        if (!mapRef.current || !isMapLoaded) return;

        // Remove existing start marker
        if (startMarkerRef.current) {
            startMarkerRef.current.remove();
            startMarkerRef.current = null;
        }

        // Remove existing end marker
        if (endMarkerRef.current) {
            endMarkerRef.current.remove();
            endMarkerRef.current = null;
        }

        let rotationAngle = 0;
        const shouldShowStartIconAndTooltip = useStartMarkerIcon && startPoint && endPoint && startPoint.lat && startPoint.lng && endPoint.lat && endPoint.lng;

        if (shouldShowStartIconAndTooltip) {
            rotationAngle = calculateBearing(startPoint.lat, startPoint.lng, endPoint.lat, endPoint.lng);
        }

        // Add start marker (only if showStartMarker is true)
        if (showStartMarker && startPoint && startPoint.lng) {
            if (useSimpleStartMarker) {
                // Show simple dot marker for origin when there's a current location tracking
                startMarkerRef.current = addMarker(mapRef.current, startPoint, startMarkerImg, startPoint.name, false, '#17B26A', false, 0, true);
            } else if (useStartMarkerIcon) {
                startMarkerRef.current = addMarker(mapRef.current, startPoint, startMarkerImg, startPoint.name, showImageAsMarker, startMarkerColor, true, rotationAngle, true);
            } else {
                startMarkerRef.current = addMarker(mapRef.current, startPoint, startMarkerImg, null, showImageAsMarker, startMarkerColor, false, 0, false);
            }
        }

        if (endPoint && endPoint.lng) {
            const showTooltip = showEndPointTooltip || useStartMarkerIcon;
            endMarkerRef.current = addMarker(mapRef.current, endPoint, startMarkerImg, endPoint.name, showImageAsMarker, endMarkerColor, false, 0, showTooltip);
        }
    }, [
        startPoint,
        endPoint,
        showImageAsMarker,
        addMarker,
        startMarkerColor,
        endMarkerColor,
        isMapLoaded,
        mapRef.current,
        useStartMarkerIcon,
        showStartMarker,
        useSimpleStartMarker,
        showEndPointTooltip,
    ]);

    // Clear existing route layers and sources
    const clearRoutes = useCallback(() => {
        if (!mapRef.current) return;

        // Remove existing route layers and sources
        ['route', 'fetched-route'].forEach((id) => {
            if (mapRef.current?.getLayer(id)) {
                mapRef.current.removeLayer(id);
            }

            if (mapRef.current?.getSource(id)) {
                mapRef.current.removeSource(id);
            }
        });
    }, []);

    // Function to update current location marker - snap to route and rotate towards destination
    const updateCurrentLocationMarker = useCallback(() => {
        if (!mapRef.current || !isMapLoaded) return;

        // Remove existing current location marker
        if (currentLocationMarkerRef.current) {
            currentLocationMarkerRef.current.remove();
            currentLocationMarkerRef.current = null;
        }

        // Add current location marker if provided
        if (currentLocation) {
            let markerCoords: [number, number] = [currentLocation.lng, currentLocation.lat];

            // Try to snap to route if route exists
            const routeSource = mapRef.current.getSource('route') as mapboxgl.GeoJSONSource;
            const routeData = routeSource?._data as any;
            const routeCoords = routeData?.geometry?.coordinates;

            if (routeCoords && routeCoords.length > 0) {
                try {
                    const snappedCoords = findClosestCoord([currentLocation.lng, currentLocation.lat], routeCoords);
                    markerCoords = snappedCoords as [number, number];
                } catch (error: any) {
                    console.warn('Failed to snap current location to route', error);
                }
            }

            // Calculate bearing towards end point for rotation
            let rotationAngle = 0;

            if (endPoint?.lat && endPoint?.lng) {
                rotationAngle = calculateBearing(markerCoords[1], markerCoords[0], endPoint.lat, endPoint.lng);
            }

            const svgMarker = createSVGMarker('#6172F3', rotationAngle);
            currentLocationMarkerRef.current = new mapboxgl.Marker(svgMarker).setLngLat(markerCoords).addTo(mapRef.current!);
        }
    }, [currentLocation, isMapLoaded, endPoint]);

    // Update route and markers on map
    const updateRouteAndMarkers = useCallback(async () => {
        if (!mapRef.current || !isShowRoute || !startPoint?.lng) return;

        let allCoordinates: [number, number][] = [];

        try {
            if (midpoints && midpoints.length > 0 && endPoint?.lat && endPoint?.lng) {
                const validMidpoints = midpoints.filter((mp) => mp?.lat && mp?.lng);

                if (validMidpoints.length > 0) {
                    const routeSegments = [];
                    let currentPoint = startPoint;

                    for (const waypoint of validMidpoints) {
                        const segment = await getDrivingRouteCoordinates(currentPoint, waypoint, t, preventCrossBorder, clearRoutes, updatePointMarkers);
                        routeSegments.push(segment);
                        currentPoint = waypoint;
                    }

                    const finalSegment = await getDrivingRouteCoordinates(currentPoint, endPoint, t, preventCrossBorder, clearRoutes, updatePointMarkers);
                    routeSegments.push(finalSegment);

                    allCoordinates = routeSegments.flat();
                }
            } else if (midpoint?.lat && midpoint?.lng && endPoint?.lat && endPoint?.lng) {
                const [route1, route2] = await Promise.all([
                    getDrivingRouteCoordinates(startPoint, midpoint, t, preventCrossBorder, clearRoutes, updatePointMarkers),
                    getDrivingRouteCoordinates(midpoint, endPoint, t, preventCrossBorder, clearRoutes, updatePointMarkers),
                ]);

                allCoordinates = [...route1, ...route2];
            } else if (endPoint?.lat && endPoint?.lng) {
                // Direct route: start → end
                const route = await getDrivingRouteCoordinates(startPoint, endPoint, t, preventCrossBorder, clearRoutes, updatePointMarkers);
                allCoordinates = route;
            }

            if (allCoordinates.length > 0) {
                // Add route source and layer
                mapRef.current.addSource('route', {
                    type: 'geojson',
                    data: {
                        type: 'Feature',
                        properties: {},
                        geometry: {
                            type: 'LineString',
                            coordinates: allCoordinates,
                        },
                    },
                });

                mapRef.current.addLayer({
                    id: 'route',
                    type: 'line',
                    source: 'route',
                    layout: {
                        'line-join': 'round',
                        'line-cap': 'round',
                    },
                    paint: {
                        'line-color': '#444CE7',
                        'line-width': 3,
                        'line-opacity': 0.75,
                    },
                });

                // Fit bounds to show entire route
                const bounds = new mapboxgl.LngLatBounds();
                allCoordinates.forEach((coord) => bounds.extend(coord));

                mapRef.current.fitBounds(bounds, {
                    padding: { top: 50, bottom: 50, left: 50, right: 50 },
                    maxZoom: 10,
                });

                setTimeout(() => {
                    updateMidpointMarkers();
                    updateCurrentLocationMarker();
                }, 50);

                // Calculate distance and duration after route is rendered (non-blocking)
                if (setDistance || setDuration) {
                    setTimeout(async () => {
                        try {
                            let totalDistance = 0;
                            let totalDuration = 0;

                            if (midpoints && midpoints.length > 0 && endPoint?.lat && endPoint?.lng) {
                                const validMidpoints = midpoints.filter((mp) => mp?.lat && mp?.lng);

                                if (validMidpoints.length > 0) {
                                    let currentPoint = startPoint;

                                    for (const waypoint of validMidpoints) {
                                        const stats = await getRouteStats(currentPoint, waypoint, t, preventCrossBorder);
                                        totalDistance += stats.distance;
                                        totalDuration += stats.duration;
                                        currentPoint = waypoint;
                                    }

                                    const finalStats = await getRouteStats(currentPoint, endPoint, t, preventCrossBorder);
                                    totalDistance += finalStats.distance;
                                    totalDuration += finalStats.duration;
                                }
                            } else if (midpoint?.lat && midpoint?.lng && endPoint?.lat && endPoint?.lng) {
                                const [stats1, stats2] = await Promise.all([getRouteStats(startPoint, midpoint, t, preventCrossBorder), getRouteStats(midpoint, endPoint, t, preventCrossBorder)]);
                                totalDistance = stats1.distance + stats2.distance;
                                totalDuration = stats1.duration + stats2.duration;
                            } else if (endPoint?.lat && endPoint?.lng) {
                                const stats = await getRouteStats(startPoint, endPoint, t, preventCrossBorder);
                                totalDistance = stats.distance;
                                totalDuration = stats.duration;
                            }

                            // Distance/duration semantics differ by context (ported from WAL's
                            // Orders/Common/MapBox/MapBoxMap so the order Tracking widget shows the SAME
                            // numbers as WAL for the same order):
                            //  - In-transit (currentLocation present): `midpoints` are the carrier's
                            //    location HISTORY, so the summed total above is "everything driven so far
                            //    + the rest", which inflates as pings accumulate/loop. Snap currentLocation
                            //    to the nearest vertex of the already-drawn polyline, slice from there to
                            //    the end and measure that with turf — no extra Directions call. Duration is
                            //    scaled by the remaining/total ratio and labelled as an ETA.
                            //  - Planning (no currentLocation — quotes, booked orders): midpoints are
                            //    planned stops, so the summed total IS the trip and is left untouched.
                            const hasLiveLocation = !!(currentLocation?.lat && currentLocation?.lng);
                            let displayDistanceMeters = totalDistance;
                            let displayDuration = totalDuration;

                            if (hasLiveLocation && allCoordinates.length >= 2) {
                                const livePoint = turf.point([currentLocation.lng, currentLocation.lat]);
                                const closestIdx = allCoordinates.reduce((bestIdx, coord, idx) => {
                                    const d = turf.distance(livePoint, turf.point(coord), { units: 'meters' });
                                    const bestD = turf.distance(livePoint, turf.point(allCoordinates[bestIdx]), { units: 'meters' });

                                    return d < bestD ? idx : bestIdx;
                                }, 0);
                                const remainingCoords = allCoordinates.slice(closestIdx);

                                if (remainingCoords.length >= 2) {
                                    displayDistanceMeters = turf.length(turf.lineString(remainingCoords), { units: 'kilometers' }) * 1000;
                                    displayDuration = totalDistance > 0 ? (displayDistanceMeters / totalDistance) * totalDuration : 0;
                                }
                            }

                            if (setDistance) {
                                const routeDistance = Math.round((displayDistanceMeters / 1000) * 0.621371); // Convert to miles
                                setDistance(routeDistance);
                            }

                            if (setDuration) {
                                const hours = Math.floor(displayDuration / 3600);
                                const minutes = Math.floor((displayDuration % 3600) / 60);
                                // WAL labels the remaining time "ETA …"; planning routes keep the plain duration.
                                const formattedDuration = `${hasLiveLocation ? 'ETA ' : ''}${hours} hr ${minutes} min`;
                                setDuration(formattedDuration);
                            }

                            if (setIsRouteLoading) {
                                setIsRouteLoading(false);
                            }
                        } catch (error: any) {
                            await logError(error, {
                                errorMessage: error?.message || t('MapBox.Error_calculating_route_stats'),
                                errorLocation: 'OrderView: MapBoxMap: updateRouteAndMarkers',
                                storeInDatabase: true,
                                logToConsole: true,
                            });
                        }
                    }, 100); // Small delay to ensure route renders first
                }
            }
        } catch (error: any) {
            await logError(error, {
                errorMessage: error?.message || t('MapBox.Error_updating_route'),
                errorLocation: 'OrderView: MapBoxMap: updateRouteAndMarkers',
                storeInDatabase: true,
                logToConsole: true,
            });
        }
    }, [startPoint, endPoint, midpoint, midpoints, isShowRoute, clearRoutes, preventCrossBorder, updateCurrentLocationMarker, currentLocation]);

    // Update current location marker when dependencies change
    useEffect(() => {
        updateCurrentLocationMarker();
    }, [updateCurrentLocationMarker]);

    const initializeMap = useCallback(() => {
        if (!window?.MAPBOX_ACCESSTOKEN) {
            console.error(t('MapBox.Mapbox_access_token_missing'));

            return;
        }

        mapboxgl.accessToken = window.MAPBOX_ACCESSTOKEN;

        mapRef.current = new mapboxgl.Map({
            container: mapContainerRef.current,
            style: 'mapbox://styles/mapbox/light-v11',
            center: [-95, 55],
            zoom: defaultZoom,
            pitch: 0,
            bearing: 0,
            boxZoom: isZoomable,
            antialias: true,
            dragPan: isDraggable,
            scrollZoom: isZoomable,
            doubleClickZoom: isZoomable,
            touchZoomRotate: isZoomable,
            projection: 'mercator',
            attributionControl: false,
            dragRotate: false,
        });

        updatePointMarkers();

        if (isZoomControlVisible) {
            mapRef.current.addControl(new mapboxgl.NavigationControl());
        }

        // Fullscreen control
        if (isFullscreen) {
            const fullscreenControl = new mapboxgl.FullscreenControl();
            mapRef.current.addControl(fullscreenControl);
        }

        mapRef.current.on('load', async () => {
            setIsMapLoaded(true);
        });
    }, [defaultZoom, isZoomable, isDraggable, isZoomControlVisible, isFullscreen, t, isFullscreenMode]);

    useEffect(() => {
        initializeMap();

        return () => {
            setIsMapLoaded(false);
            // The map (and with it the route layer) is gone — the next mount must redraw even
            // though the signature is unchanged.
            drawnRouteSignatureRef.current = null;

            if (startMarkerRef.current) {
                startMarkerRef.current.remove();
                startMarkerRef.current = null;
            }

            if (endMarkerRef.current) {
                endMarkerRef.current.remove();
                endMarkerRef.current = null;
            }

            if (currentLocationMarkerRef.current) {
                currentLocationMarkerRef.current.remove();
                currentLocationMarkerRef.current = null;
            }

            if (mapRef.current) {
                mapRef.current.remove();
                mapRef.current = null;
            }
        };
    }, [initializeMap]);
    // Update routes and fit bounds when points change
    useEffect(() => {
        if (!mapRef.current || !isMapLoaded) return;
        // Same route as the one already drawn — redrawing it would only make it blink.
        if (drawnRouteSignatureRef.current === routeSignature) return;
        drawnRouteSignatureRef.current = routeSignature;

        const updateMapView = async () => {
            await updateRouteAndMarkers();
            updatePointMarkers();

            // Set view based on points
            if (!isShowRoute) {
                const bounds = new mapboxgl.LngLatBounds();

                if (startPoint && startPoint.lng) {
                    bounds.extend([startPoint.lng, startPoint.lat]);
                }

                if (endPoint && endPoint.lng) {
                    bounds.extend([endPoint.lng, endPoint.lat]);
                }

                if (startPoint && endPoint) {
                    mapRef?.current?.fitBounds(bounds, {
                        padding: { top: 50, bottom: 50, left: 50, right: 50 },
                        maxZoom: 10,
                    });
                } else if (startPoint) {
                    mapRef?.current?.setCenter([startPoint.lng, startPoint.lat]);
                    mapRef?.current?.setZoom(defaultZoom > 16 ? defaultZoom : 16);
                } else if (endPoint) {
                    mapRef?.current?.setCenter([endPoint.lng, endPoint.lat]);
                    mapRef?.current?.setZoom(defaultZoom > 16 ? defaultZoom : 16);
                }
            }
        };

        updateMapView();
    }, [isMapLoaded, routeSignature, startPoint, endPoint, midpoints, isShowRoute, updateRouteAndMarkers, defaultZoom]);

    useEffect(
        () => () => {
            if (markerRef?.current) {
                if (Array.isArray(markerRef.current)) {
                    markerRef.current.forEach((marker: mapboxgl.Marker) => marker?.remove());
                } else {
                    markerRef.current.remove();
                }
                markerRef.current = null;
            }
        },
        []
    );

    return (
        <div
            ref={containerRef}
            style={{
                position: 'relative',
                width: '100%',
                height: isFullscreenMode ? '100vh' : typeof height === 'string' ? height : `${height}px`,
            }}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
        >
            <div id="map-container" ref={mapContainerRef} style={{ width: '100%', height: '100%' }} className={className} />

            {/* Custom Map Controls */}
            {showMapControls && (
                <div className="absolute top-2 right-2 flex flex-col gap-2 max-md:gap-1.5">
                    {/* Mobile: 28px buttons / 16px icons, matching the radar map controls */}
                    {/* Zoom Controls */}
                    {showZoomControls || isFullscreenMode ? (
                        <div className="bg-white rounded-lg max-md:rounded-md shadow-md border border-utilityGray200 overflow-hidden">
                            <button
                                onClick={handleZoomIn}
                                className="w-9 h-9 max-md:w-7 max-md:h-7 flex items-center justify-center hover:bg-gray50 transition-colors border-b border-utilityGray200"
                                title="Zoom In"
                                type="button"
                            >
                                <Plus className="w-5 h-5 max-md:w-4 max-md:h-4 text-gray500" />
                            </button>
                            <button onClick={handleZoomOut} className="w-9 h-9 max-md:w-7 max-md:h-7 flex items-center justify-center hover:bg-gray50 transition-colors" title="Zoom Out" type="button">
                                <Minus className="w-5 h-5 max-md:w-4 max-md:h-4 text-gray500" />
                            </button>
                        </div>
                    ) : (
                        <></>
                    )}

                    {/* Fullscreen Control */}
                    <div className="bg-white rounded-lg max-md:rounded-md shadow-md border border-utilityGray200 overflow-hidden">
                        <button
                            onClick={toggleFullscreen}
                            className="w-9 h-9 max-md:w-7 max-md:h-7 flex items-center justify-center hover:bg-gray50 transition-colors"
                            title={isFullscreenMode ? t('Common.Exit_Fullscreen') : t('Common.Enter_Fullscreen')}
                            type="button"
                        >
                            {isFullscreenMode ? <Minimize02 className="w-5 h-5 max-md:w-4 max-md:h-4 text-gray500" /> : <Maximize02 className="w-5 h-5 max-md:w-4 max-md:h-4 text-gray500" />}
                        </button>
                    </div>

                    <div className="bg-white rounded-lg max-md:rounded-md shadow-md border border-utilityGray200 overflow-hidden">
                        <button onClick={handleResetZoom} className="w-9 h-9 max-md:w-7 max-md:h-7 flex items-center justify-center hover:bg-gray50 transition-colors" title="Reset Zoom" type="button">
                            <Maximize01 className="w-5 h-5 max-md:w-4 max-md:h-4 text-gray500" />
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default MapboxMap;
